import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp, Settings } from 'lucide-react';
import styles from './ComparisonControls.module.css';

interface ComparisonControlsProps {
  isComparisonMode: boolean;
  onComparisonToggle: (enabled: boolean) => void;
  trimEmptyData: boolean;
  onTrimEmptyDataToggle: (enabled: boolean) => void;
  comparableEntities: Array<{ id: string; name: string; year?: number; color?: string }>;
  visibleEntities: Record<string, boolean>;
  onEntityVisibilityChange: (entityId: string, visible: boolean) => void;
  maxVisibleItems?: number;
  className?: string;
}

export const ComparisonControls: React.FC<ComparisonControlsProps> = ({
  isComparisonMode,
  onComparisonToggle,
  trimEmptyData,
  onTrimEmptyDataToggle,
  comparableEntities,
  visibleEntities,
  onEntityVisibilityChange,
  maxVisibleItems = 5,
  className
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const settingsWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showSettings) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      if (settingsWrapperRef.current && !settingsWrapperRef.current.contains(e.target as Node)) {
        setShowSettings(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettings]);

  // Auto-collapse on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const visibleCount = Object.values(visibleEntities).filter(Boolean).length;
  const canToggleMore = visibleCount < maxVisibleItems;

  return (
    <div className={`${styles.container} ${className || ''}`}>
      <div className={styles.header}>
        <label className={styles.comparisonToggle}>
          <input
            type="checkbox"
            checked={isComparisonMode}
            onChange={(e) => onComparisonToggle(e.target.checked)}
          />
          <span>Compare with previous years</span>
        </label>
        
        {isComparisonMode && (
          <>
            <div ref={settingsWrapperRef} className={styles.settingsWrapper}>
              <button
                className={styles.settingsButton}
                onClick={() => setShowSettings(!showSettings)}
                aria-label="Comparison settings"
                title="Comparison settings"
              >
                <Settings size={16} />
              </button>
              
              {showSettings && (
                <div className={styles.settingsPopup}>
                  <label className={styles.settingOption}>
                    <input
                      type="checkbox"
                      checked={trimEmptyData}
                      onChange={(e) => onTrimEmptyDataToggle(e.target.checked)}
                    />
                    <span>Trim empty leading data</span>
                  </label>
                  <div className={styles.settingDescription}>
                    Automatically hide empty days before the first activity
                  </div>
                </div>
              )}
            </div>
            
            <button
              className={styles.collapseButton}
              onClick={() => setIsCollapsed(!isCollapsed)}
              aria-expanded={!isCollapsed}
              aria-label={isCollapsed ? 'Expand comparison panel' : 'Collapse comparison panel'}
              title={isCollapsed ? 'Expand comparison panel' : 'Collapse comparison panel'}
            >
              {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          </>
        )}
      </div>

      {isComparisonMode && !isCollapsed && comparableEntities.length > 0 && (
        <div className={styles.entitiesList}>
          <div className={styles.entitiesHeader}>
            <span>Show/Hide Records</span>
            {visibleCount >= maxVisibleItems && (
              <span className={styles.limitWarning}>Max {maxVisibleItems} visible</span>
            )}
          </div>
          <div className={styles.entities}>
            {comparableEntities.slice(0, 10).map((entity) => {
              const isChecked = visibleEntities[entity.id] !== false;
              const isDisabled = !isChecked && !canToggleMore;
              
              return (
                <label 
                  key={entity.id} 
                  className={`${styles.entityCheckbox} ${isDisabled ? styles.disabled : ''}`}
                  title={isDisabled ? `Maximum ${maxVisibleItems} entities can be displayed` : ''}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => onEntityVisibilityChange(entity.id, e.target.checked)}
                    disabled={isDisabled}
                  />
                  {entity.color && (
                    <span
                      className={styles.colorIndicator}
                      style={{ backgroundColor: entity.color }}
                    />
                  )}
                  <span className={styles.entityLabel}>
                    {entity.year}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonControls;
